import { useState } from "react";
import { Minus, Plus, CheckCircle2, MessageCircle, Send } from "lucide-react";

const WA_NUMBER = "524424319100";

const IMAGES = [
  {
    url: "https://customer-assets.emergentagent.com/job_industrial-shields/artifacts/5tjnb3kq_tsc-wiper-bucket-%281%29.webp",
    alt: "Perfil rascador CL1 vista 3D",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_industrial-shields/artifacts/2oehvf2h_H92a83b4b21144f1f9f27cfca6ad1fdd6v.jpg",
    alt: "Detalle del labio rascador CL1",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_industrial-shields/artifacts/4ypp9vr9_H28f77fff69384170a3928da661152ae5G.jpg",
    alt: "Rollos continuos de rascador CL1",
  },
];

const FEATURES = [
  "Se adapta fácilmente a superficies irregulares de hasta 3 mm",
  "Sellado perfecto garantizado",
  "Altamente confiables y resistentes a todos los refrigerantes estándar",
  "Disponibles en longitudes continuas",
];

export default function Products() {
  const [qty, setQty] = useState(1);
  const [selectedImg, setSelectedImg] = useState(0);

  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => q + 1);

  const cotizarWhatsApp = () => {
    const msg = encodeURIComponent(
      `Hola Guarda Flex, quiero cotizar el siguiente producto:\n\n` +
        `• Limpiaparabrisas CL1\n` +
        `• Cantidad solicitada: ${qty} metro${qty > 1 ? "s" : ""}\n\n` +
        `Por favor envíenme cotización con disponibilidad, tiempo de entrega y forma de pago. Gracias.`
    );
    window.open(`https://wa.me/${WA_NUMBER}?text=${msg}`, "_blank");
  };

  const cotizarFormulario = () => {
    const el = document.getElementById("contacto");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    // pre-fill via custom event would require deeper integration; user goes to form
  };

  return (
    <section
      id="productos"
      data-testid="products-section"
      className="gf-section bg-white relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-12 h-px bg-[#00B4D8]" />
          <span className="gf-overline">Tienda · Producto destacado</span>
        </div>
        <h2 className="font-display font-black text-[#0A1B35] text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-12 max-w-3xl">
          Solicita cotización: <span className="text-[#00B4D8]">Limpiaparabrisas CL1</span>
        </h2>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Image gallery */}
          <div className="min-w-0 lg:col-span-7">
            <div
              className="relative aspect-[4/3] bg-slate-50 border border-slate-200 overflow-hidden"
              style={{ borderRadius: "2px" }}
              data-testid="product-main-image"
            >
              <img
                src={IMAGES[selectedImg].url}
                alt={IMAGES[selectedImg].alt}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 bg-[#0A1B35] text-white px-3 py-1 font-mono-accent text-[10px] uppercase tracking-[0.25em]">
                CL1 · CNC Wiper
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              {IMAGES.map((im, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImg(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`aspect-[4/3] overflow-hidden border-2 transition-all ${
                    selectedImg === i
                      ? "border-[#00B4D8]"
                      : "border-slate-200 hover:border-slate-400"
                  }`}
                  style={{ borderRadius: "2px" }}
                >
                  <img src={im.url} alt={im.alt} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Info + qty */}
          <div className="min-w-0 lg:col-span-5">
            <div
              className="border border-slate-200 p-8 bg-white sticky top-28"
              style={{ borderRadius: "2px" }}
            >
              <h3 className="font-display font-black text-xl sm:text-2xl text-[#0A1B35] mb-3 tracking-tight break-words">
                Limpiaparabrisas CL1
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Los rascadores de tipo CL1 están diseñados específicamente para adaptarse a
                grandes superficies y a sistemas de faldones de aluminio, pero pueden utilizarse
                en una amplia variedad de aplicaciones.
              </p>

              <ul className="space-y-2.5 mb-8">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <CheckCircle2 size={16} className="text-[#00B4D8] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="border-t border-slate-200 pt-6 mb-6">
                <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">
                  Precio bajo cotización
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Indica la cantidad que necesitas y te enviaremos una cotización personalizada
                  con disponibilidad y tiempo de entrega.
                </p>
              </div>

              {/* Qty selector */}
              <div className="mb-6">
                <label className="gf-label mb-3">Cantidad (metros)</label>
                <div
                  className="flex items-stretch border border-slate-300"
                  style={{ borderRadius: "2px" }}
                >
                  <button
                    onClick={dec}
                    disabled={qty <= 1}
                    data-testid="product-qty-decrease"
                    className="w-12 flex items-center justify-center hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Disminuir"
                  >
                    <Minus size={16} />
                  </button>
                  <input
                    type="number"
                    min={1}
                    value={qty}
                    onChange={(e) => {
                      const v = parseInt(e.target.value || "1", 10);
                      if (!isNaN(v)) setQty(Math.max(1, v));
                    }}
                    data-testid="product-qty-input"
                    className="flex-1 text-center font-display font-bold text-xl text-[#0A1B35] outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    onClick={inc}
                    data-testid="product-qty-increase"
                    className="w-12 flex items-center justify-center hover:bg-slate-100 transition-colors"
                    aria-label="Aumentar"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                  Sin límite de cantidad. Pide tantos metros como necesites.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  onClick={cotizarWhatsApp}
                  data-testid="product-quote-whatsapp"
                  className="gf-btn-primary justify-center"
                >
                  <MessageCircle size={16} /> Cotizar por WhatsApp
                </button>
                <button
                  onClick={cotizarFormulario}
                  data-testid="product-quote-form"
                  className="gf-btn-secondary justify-center"
                >
                  <Send size={16} /> Cotizar por formulario
                </button>
              </div>
              <p className="text-[11px] text-slate-400 text-center mt-3">
                Tu solicitud llega directo a un asesor técnico de Guarda Flex.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
